<i18n locale="en">
  {
    "actions": {
      "immediate": "Send Now",
      "scheduled": "Schedule"
    },
    "errors": {
      "customer-count": "There was a problem on our end and we can’t send this right now"
    },
    "messages": {
      "loading": "Calculating number of customers for campaign...",
      "immediate": "No customers are targeted for this campaign | You’re about to send this to 1 customer | You’re about to send this to {formattedCount} customers",
      "scheduled": "No customers are targeted for this campaign | You’re about to schedule this for 1 customer | You’re about to schedule this for {formattedCount} customers"
    }
  }
  </i18n>

<template>
  <div class="flex-row gap-3 align-center justify-end">
    <OwnType :text="customerMessage" align="right" />
    <OwnButton
      class="text--no-wrap"
      data-testid="schedule-campaign-button"
      :disabled="customerCount === 0 || isLoadingCustomerCount || disabled"
      :text="t(`actions.${type}`)"
      :processing="saving"
      variant="primary"
      @click="schedule"
    />
  </div>
</template>

<script>
import { isFuture } from 'date-fns'
import { useI18n } from 'vue-i18n'

import { NumericFormatter } from '@/format'
import { logError } from '@/logger'
import notify from '@/mixins/notify'
import { OlympusClient } from '@/OlympusClient'
import { OwnButton } from '@/ui/OwnButton'
import { OwnType } from '@/ui/OwnType'

export default {
  name: 'ScheduleOTCFooter',
  components: { OwnButton, OwnType },
  mixins: [notify],
  props: {
    /** The current campaign */
    campaign: { type: Object, required: true },

    /** Current scheduled date for the campaign */
    date: { type: Date, required: true },

    /** Disables scheduling */
    disabled: { type: Boolean, required: true },

    /** Current loading state */
    saving: { type: Boolean, default: false },
  },
  emits: ['schedule'],
  data() {
    return {
      customerCount: 0,
      isLoadingCustomerCount: false,
      isScheduling: false,
    }
  },
  setup() {
    const { t } = useI18n()

    return { t }
  },
  computed: {
    customerMessage() {
      const {
        customerCount: count,
        isLoadingCustomerCount,
        type,
        disabled,
      } = this

      if (isLoadingCustomerCount) return this.t('messages.loading')

      if (disabled) return ''

      return this.t(`messages.${type}`, {
        count,
        formattedCount: NumericFormatter(count),
      })
    },
    type() {
      return isFuture(this.date) ? 'scheduled' : 'immediate'
    },
  },
  mounted() {
    this.loadCustomerCount()
  },
  methods: {
    async loadCustomerCount() {
      const { campaign } = this

      try {
        this.isLoadingCustomerCount = true

        const { eligible } =
          await OlympusClient.marketing.v1.campaigns.campaign.getEligibleParticipants(
            { params: { campaignId: campaign.id } }
          )

        this.customerCount = eligible
      } catch (error) {
        logError(error)
        this.$notify(this.t('errors.customer-count'), 'error')
      } finally {
        this.isLoadingCustomerCount = false
      }
    },
    async schedule() {
      this.$emit('schedule')
    },
  },
}
</script>
